import { eq, useLiveQuery } from '@tanstack/react-db'
import { applyAddendums, orderAddendums } from '@/db/application/addendums'
import { activeLogs } from '@/db/application/callouts'

import type { CalloutNotesSectionNote } from '@/components/CalloutNotesSection'
import { useDb } from '@/lib/dbContext'
import type { Addendum, AddendumChange, Customer, Job, Note, StatusChangeLog } from '@/lib/schemas/db'
import { withSpan } from '@/lib/sentry'
import { formatRelativeDateTime } from '@/lib/relativeDateTime'
import type { InvoiceServiceScreenProps } from '@/screens/InvoiceServiceScreen'
import type { BillingOutcome } from '@/screens/InvoiceServiceScreen'
import { looksLikePublicId } from '@/lib/public-id/routeLookup'
import { useEnsureServiceLoaded } from '@/screens/useEnsureServiceLoaded'
import { captureLegacyIdRoute } from '@/lib/public-id/legacyRouteSentry'

type InvoiceState =
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'not_found' }
  | { kind: 'ready'; screenProps: InvoiceServiceScreenProps }

function logsToNotes(
  logs: Note[] | undefined,
  statusChanges: StatusChangeLog[] | undefined,
  getAuthorDisplayName: (authorId: string) => string,
): CalloutNotesSectionNote[] {
  // Soft-deleted notes are no longer part of the record; the audit trail for
  // them lives on the Log Work feed.
  const noteItems: CalloutNotesSectionNote[] = activeLogs(logs ?? []).map((log) =>
    log.photoUrl
      ? {
          id: log.id,
          kind: 'photo',
          authorName: getAuthorDisplayName(log.authorId),
          createdAt: log.createdAt,
          photoUrl: log.photoUrl ?? '',
          photoUrls: log.photoUrls,
          caption: log.caption ?? '',
        }
      : {
          id: log.id,
          kind: 'text',
          authorName: getAuthorDisplayName(log.authorId),
          createdAt: log.createdAt,
          text: log.text ?? '',
        },
  )

  const statusItems: CalloutNotesSectionNote[] = (statusChanges ?? []).map((change) => ({
    id: change.id,
    kind: 'status_change',
    authorName: getAuthorDisplayName(change.authorId),
    createdAt: change.createdAt,
    previousStatus: change.previousStatus,
    newStatus: change.newStatus,
    reason: change.reason,
  }))

  return [...noteItems, ...statusItems].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )
}

export function useJobInvoiceService(
  jobIdentity: string,
  onDone: () => void,
): InvoiceState {
  const db = useDb()
  const tryPublicId = looksLikePublicId(jobIdentity)

  const jobByIdResult = useLiveQuery(
    (q) =>
      jobIdentity
        ? q
            .from({ jobs: db.jobsCollection })
            .where(({ jobs }) => eq(jobs.id, jobIdentity))
            .findOne()
        : null,
    [jobIdentity],
  )

  const jobByPublicIdResult = useLiveQuery(
    (q) =>
      jobIdentity && tryPublicId
        ? q
            .from({ jobs: db.jobsCollection })
            .where(({ jobs }) => eq(jobs.publicId, jobIdentity))
            .findOne()
        : null,
    [jobIdentity, tryPublicId],
  )

  const job =
    (jobByIdResult.data as Job | undefined) ??
    (jobByPublicIdResult.data as Job | undefined)
  const jobId = job?.id ?? jobIdentity
  if (!tryPublicId && job && (jobByIdResult.data as Job | undefined)) {
    captureLegacyIdRoute('job', jobIdentity)
  }

  const customerResult = useLiveQuery(
    (q) => {
      if (!job?.customerId) return null
      return q
        .from({ customers: db.customersCollection })
        .where(({ customers }) => eq(customers.id, job.customerId))
        .findOne()
    },
    [job?.customerId],
  )

  const usersResult = useLiveQuery((q) => q.from({ users: db.usersCollection }))

  const notesResult = useLiveQuery(
    (q) =>
      jobId
        ? q
            .from({ notes: db.jobNotesCollection })
            .where(({ notes }) => eq(notes.serviceId, jobId))
            .orderBy(({ notes }) => notes.createdAt)
        : null,
    [jobId],
  )

  const statusChangesResult = useLiveQuery(
    (q) =>
      jobId
        ? q
            .from({ statusChanges: db.jobStatusChangesCollection })
            .where(({ statusChanges }) => eq(statusChanges.serviceId, jobId))
            .orderBy(({ statusChanges }) => statusChanges.createdAt)
        : null,
    [jobId],
  )

  const addendumsResult = useLiveQuery(
    (q) =>
      jobId
        ? q
            .from({ addendums: db.jobAddendumsCollection })
            .where(({ addendums }) => eq(addendums.serviceId, jobId))
            .orderBy(({ addendums }) => addendums.createdAt)
        : null,
    [jobId],
  )

  // Archived (Invoiced/Cancelled) services aren't in the live sync; pull one
  // in on demand so Invoice Prep loads from a direct link too.
  const recordQueriesSettled =
    !jobByIdResult.isLoading &&
    !jobByPublicIdResult.isLoading &&
    !jobByIdResult.isError &&
    !jobByPublicIdResult.isError
  const { hydrating } = useEnsureServiceLoaded(
    'job',
    jobIdentity,
    recordQueriesSettled && !job,
  )

  if (
    hydrating ||
    jobByIdResult.isLoading ||
    jobByPublicIdResult.isLoading ||
    customerResult.isLoading ||
    usersResult.isLoading ||
    notesResult.isLoading ||
    statusChangesResult.isLoading ||
    addendumsResult.isLoading
  ) {
    return { kind: 'loading' }
  }

  if (
    jobByIdResult.isError ||
    jobByPublicIdResult.isError ||
    customerResult.isError ||
    usersResult.isError ||
    notesResult.isError ||
    statusChangesResult.isError ||
    addendumsResult.isError
  ) {
    return { kind: 'error' }
  }

  if (!job) return { kind: 'not_found' }

  const addendums = orderAddendums(
    (addendumsResult.data as Addendum[] | undefined) ?? [],
  )
  // The locked record keeps its values; addendums layer on top to give what is
  // in force now — including a corrected Omni invoice ID.
  const effective = applyAddendums(
    job as unknown as Record<string, unknown>,
    addendums,
  ) as unknown as typeof job

  const handleSaveAddendum = async (reason: string, changes: AddendumChange[]) => {
    await db.appendJobAddendumAndWait(job.id, { reason, changes })
  }

  const customer = customerResult.data as Customer | undefined
  const linkedCustomerName = customer
    ? customer.externalId
      ? `${customer.fullName} – ${customer.externalId}`
      : customer.fullName
    : 'Unknown customer'

  const notes = logsToNotes(
    notesResult.data as Note[] | undefined,
    statusChangesResult.data as StatusChangeLog[] | undefined,
    db.getAuthorDisplayName,
  )

  const handleMarkInvoiced = async (params: {
    omniInvoiceId?: string
    noOmniInvoice: boolean
    billingOutcome?: BillingOutcome
  }) => {
    const billingOutcome =
      params.billingOutcome ??
      (params.noOmniInvoice ? 'NoOmniInvoice' : 'OmniInvoice')
    await withSpan(
      {
        op: 'job.status.change',
        name: 'Mark job invoiced',
        attributes: { jobId: job.id, nextStatus: 'Invoiced', billingOutcome },
      },
      async () => {
        await db.updateJobAndWait(job.id, {
          status: 'Invoiced',
          billingOutcome,
          invoiceId:
            billingOutcome === 'OmniInvoice' ? params.omniInvoiceId : undefined,
        })
        onDone()
      },
    )
  }

  const handleMoveToReview = async (reason: string) => {
    const normalizedReason = reason.trim()
    if (!normalizedReason) throw new Error('Reason is required when moving a job back to ready to work')
    return withSpan(
      {
        op: 'job.status.change',
        name: 'Move job to ready to work from invoice prep',
        attributes: { jobId: job.id, nextStatus: 'ReadyToWork' },
      },
      () => db.updateJobAndWait(job.id, { status: 'ReadyToWork', statusReason: normalizedReason }),
    )
  }

  const handleMoveToBooked = async () =>
    withSpan(
      {
        op: 'job.status.change',
        name: 'Move job to ready to work from invoice prep',
        attributes: { jobId: job.id, nextStatus: 'ReadyToWork' },
      },
      () => db.updateJobAndWait(job.id, { status: 'ReadyToWork' }),
    )

  return {
    kind: 'ready',
    screenProps: {
      serviceType: 'Job',
      resourceLabel: 'Job',
      publicId: job.publicId,
      customerName: linkedCustomerName,
      customerId: job.customerId,
      customerPublicId: customer?.publicId,
      visitAddress: customer?.contactAddress,
      siteContact: customer?.fullName,
      contactPhone: customer?.contactPhone,
      contactEmail: customer?.contactEmail,
      invoiceTo: customer?.invoiceName || customer?.fullName || linkedCustomerName,
      invoiceEmail: customer?.invoiceEmail,
      invoicePhone: customer?.invoicePhone,
      invoiceAddress: customer?.invoiceAddress,
      invoiceVat: customer?.invoiceVat,
      issueSummary: effective.fault ?? '',
      scheduledAt: formatRelativeDateTime(job.createdAt),
      calloutStatus: job.status,
      statusChanges: statusChangesResult.data as StatusChangeLog[] | undefined,
      getAuthorDisplayName: db.getAuthorDisplayName,
      omniCustomerId: customer?.externalId,
      omniInvoiceId: effective.invoiceId,
      billingOutcome: job.billingOutcome,
      notes,
      onMarkInvoiced: handleMarkInvoiced,
      onMoveToReview: handleMoveToReview,
      onMoveToBooked: handleMoveToBooked,
      lockedStatus: job.status === 'Invoiced' ? 'Invoiced' : job.status === 'Cancelled' ? 'Cancelled' : undefined,
      addendums,
      onSaveAddendum: handleSaveAddendum,
      lockedActionLabel: job.status === 'Invoiced' ? 'Move to Ready to Work' : job.status === 'Cancelled' ? 'Move to Ready to Work' : undefined,
      lockedActionTitle: 'Move this job back to Ready to Work?',
      lockedActionDescription: 'Add a short reason so the office team knows why this job is being moved back to Ready to Work.',
      lockedMessage:
        job.status === 'Invoiced'
          ? 'This job is locked. No changes can be made while it is Invoiced. Move it back to Ready to Work to unlock.'
          : undefined,
      lockedReasonLabel: 'Reason (required)',
      lockedReasonPlaceholder: 'Required. E.g. customer returned, payment issue, more work needed…',
      lockedConfirmLabel: 'Move to Ready to Work',
      lockedCancelLabel: job.status === 'Invoiced' ? 'Keep Invoiced' : 'Keep Cancelled',
      invoiceReadyStatusLabel: 'ReadyToInvoice',
    },
  }
}
